/**
 * @deprecated Użyj `AnimatedDogAvatar` z `client/src/avatar/`.
 * Cienki alias kompatybilności (SplashScene + legacy LocalDogs).
 * PLAN-V2 §10: plik zostaje w town/ bez edycji krzyżowej registry.
 * WAVE5-W4: lekka wariacja prędkości animacji i mikro-pauzy w chodzie.
 */
import Phaser from 'phaser';
import { AnimatedDogAvatar } from '../../avatar/AnimatedDogAvatar.js';

export type WalkingDogOpts = {
  breed?: string;
  /** Rozmiar wyświetlania w px (kwadrat) — mapowany na scale. */
  size?: number;
  /** Delikatny tint (NPC) — stosowany na kontenerze gdy możliwe. */
  tint?: number;
};

/**
 * @deprecated Prefer AnimatedDogAvatar (moveTo / setVelocity / playAction).
 * Zachowuje API V1: `.image`, `.update(dt, moving, dirX)`, `.destroy()`.
 */
export class WalkingDog {
  private readonly avatar: AnimatedDogAvatar;
  private readonly size: number;
  private lastMoving = false;
  /** Bazowa prędkość animacji (px/s) — lekko inna per instancja. */
  private readonly baseSpeed: number;
  /** Faza cyklu chód/pauza (sekundy). */
  private gaitT = 0;
  /** Czas do następnej mikro-pauzy / wznowienia. */
  private nextGaitSwitch: number;
  private inMicroPause = false;

  constructor(scene: Phaser.Scene, opts: WalkingDogOpts = {}) {
    const breed = opts.breed ?? 'maltese';
    this.size = opts.size ?? 64;
    const scale = this.size / 96;

    // 95–145 px/s — naturalna różnica między pieskami
    this.baseSpeed = 95 + Math.random() * 50;
    this.gaitT = Math.random() * 2;
    this.nextGaitSwitch = 1.2 + Math.random() * 2.2;

    this.avatar = new AnimatedDogAvatar({
      scene,
      x: 0,
      y: 0,
      breed,
      scale,
      depth: 0,
    });

    if (opts.tint != null) {
      // lekki tint na bazie (fallback gdy brak warstw)
      try {
        for (const k of this.avatar.container.list) {
          const any = k as unknown as { setTint?: (c: number) => void };
          if (typeof any.setTint === 'function') any.setTint(opts.tint);
        }
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * Kompatybilność Splash / V1 — kontener avatara jako „image”.
   * Splash: dog.image.setPosition / tweens.targets.
   */
  get image(): Phaser.GameObjects.Container {
    return this.avatar.container;
  }

  get avatarRef(): AnimatedDogAvatar {
    return this.avatar;
  }

  /** dirX: znak kierunku poziomego; moving: czy się przemieszcza. */
  update(dtSec: number, moving: boolean, dirX: number): void {
    this.gaitT += dtSec;

    // Mikro-pauzy w animacji chodu (rodzic nadal przesuwa root — tylko animacja)
    if (moving) {
      if (this.gaitT >= this.nextGaitSwitch) {
        this.gaitT = 0;
        if (this.inMicroPause) {
          this.inMicroPause = false;
          this.nextGaitSwitch = 1.4 + Math.random() * 2.5;
        } else {
          // krótka pauza ~0.15–0.4 s co kilka sekund chodu
          this.inMicroPause = true;
          this.nextGaitSwitch = 0.15 + Math.random() * 0.25;
        }
      }
    } else {
      this.inMicroPause = false;
      this.gaitT = 0;
      this.nextGaitSwitch = 1.2 + Math.random() * 2.2;
    }

    const animMoving = moving && !this.inMicroPause;

    if (animMoving) {
      // lekka wariacja prędkości animacji (±12%) w czasie
      const wobble = 1 + 0.12 * Math.sin(this.gaitT * 2.3);
      const speed = this.baseSpeed * wobble;
      const vx = dirX < -0.02 ? -speed : dirX > 0.02 ? speed : this.lastMoving ? speed * 0.7 : 0;
      this.avatar.setVelocity(vx || speed * 0.7, 0);
    } else {
      this.avatar.setVelocity(0, 0);
      this.avatar.stop();
    }
    this.lastMoving = moving;
    const px = this.avatar.container.x;
    const py = this.avatar.container.y;
    this.avatar.update(dtSec);
    // rodzic trzyma pozycję kontenera — nie dajemy avatara odejść
    this.avatar.container.setPosition(px, py);
  }

  destroy(): void {
    this.avatar.destroy();
  }
}
